import { PaymentPlanResource, PaymentTiming, PromotionResource } from "@/interfaces";

/**
 * The date a plan takes its first payment on.
 *
 * A plan carries its installment dates one by one — the schedule stopped being
 * implied by a start date and an interval — so the first of those dates is the
 * plan's own start.
 */
export const planFirstPaymentDate = (
  plan?: PaymentPlanResource | null
): string | null => plan?.attributes?.installment_dates?.[0] ?? null;

/**
 * Whether a plan would pay too late to earn a promotion that enforces its
 * deadline.
 *
 * False means the plan's own schedule already collects on or before the
 * deadline, so the discount is earned either way — there is nothing to ask the
 * customer, and nothing they could forfeit by keeping that schedule. Mirrors
 * ResolvePromotionPaymentDateAction::planMissesDeadline() on the API side.
 *
 * Both dates arrive as plain Y-m-d strings, which compare correctly as text.
 */
export const planMissesPromotionDeadline = (
  promotion?: PromotionResource | null,
  planStartDate?: string | null
): boolean => {
  const deadline = promotion?.attributes?.stop_at;

  if (!promotion?.attributes?.requires_payment_before_stop_at || !deadline) {
    return false;
  }

  // No start date means the plan charges immediately, so it never misses.
  if (!planStartDate) {
    return false;
  }

  return planStartDate.slice(0, 10) > deadline.slice(0, 10);
};

/**
 * A deadline-bearing discount is only given up when the plan it is kept on
 * would actually pay after the deadline.
 */
export const forfeitsPromotionDiscount = (
  promotion: PromotionResource | null | undefined,
  planStartDate: string | null | undefined,
  paymentTiming: PaymentTiming
): boolean =>
  paymentTiming === "plan_schedule" && planMissesPromotionDeadline(promotion, planStartDate);
